import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getAccountDetailById,
  getStats,
} from '../../actions/accountDetailActions';
import Loading from '../common/Loading';
import ResponsiveActionBarButtons from '../common/ResponsiveActionBarButtons';
import ActionBar from '../layout/ActionBar';
import AccountForm from './components/AccountForm';
import AccountStats from './components/AccountStats';
import AccountDetailKeyRequestsList from './components/AccountDetailKeyRequestsList';
import SlidingPane from 'react-sliding-pane';
import DisableKeys from './slideout/DisableKeys';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../hooks';
import EditAccountDetails from './slideout/EditAccountDetails';

function Account() {
  const currentUser = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [, setPageTitle] = usePageTitle(['Account', 'Keys']);

  const [account, setAccount] = useState<any>();
  const [reload] = useState(undefined);
  const [disableKeysSlideoutOpen, setDisableKeysSlideoutOpen] = useState(false);
  const [editAccountDetailsSlideoutOpen, setEditAccountDetailsSlideoutOpen] =
    useState(false);
  const [stats, setStats] = useState<any>();
  const [reloadStats, setReloadStats] = useState<any>();
  const [itemsChecked, setItemsChecked] = useState<any[]>([]);
  const [targetRequestAccountDetailIds, setTargetRequestAccountDetailIds] =
    useState<any[]>([]);

  const onDisableKeys = (accounts: any[]) => {
    setTargetRequestAccountDetailIds(accounts.map((x) => x.id));
    setDisableKeysSlideoutOpen(true);
  };

  const fetchAccount = () => {
    if (!id) return;
    getAccountDetailById(id)
      .then((record) => {
        setAccount(record);
      })
      .catch((e: Error) => {
        toast.error(e.message ?? t('Failed to get account detail'));
      });
  };

  useEffect(() => {
    if (!account && id) {
      fetchAccount();
    }
  });
  useEffect(() => {
    if (id) {
      getStats(id)
        .then((d: any) => {
          setStats(d);
          setPageTitle([d.name, 'Account', 'Keys']);
        })
        .catch((e) => {
          console.error(e);
          toast.error(t('Failed to get account detail stats'));
        });
    }
  }, [id, reloadStats, setPageTitle, t]);

  return account ? (
    <>
      <ActionBar
        breadcrumb={[
          { link: '/keys/accounts', label: t('Accounts') },
          { label: account.login },
        ]}
      >
        <ResponsiveActionBarButtons
          buttons={[
            <Button
              key="disableKeys"
              className="btn btn-danger d-none d-md-inline-flex ms-2"
              onClick={() => {
                setTargetRequestAccountDetailIds([]);
                setItemsChecked([]);
                setDisableKeysSlideoutOpen(true);
              }}
              text={t('Disable all keys')}
            />,
            <Button
              key="editAccountDetails"
              className="btn btn-primary d-none d-md-inline-flex ms-2"
              onClick={() => setEditAccountDetailsSlideoutOpen(true)}
              text={t('Edit')}
            />,
          ]}
        />
      </ActionBar>
      <AccountStats id={id} stats={stats} />
      <AccountForm account={account} />
      <AccountDetailKeyRequestsList
        id={id}
        currentUser={currentUser}
        reload={reload}
        itemsChecked={itemsChecked}
        setItemsChecked={setItemsChecked}
        onDisableKeys={onDisableKeys}
      />
      <SlidingPane
        isOpen={disableKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setDisableKeysSlideoutOpen(false)}
      >
        <DisableKeys
          title={t('Disable keys for {account}', { account: account.login })}
          targetAccountDetailId={id}
          targetKeyRequestAccountDetail={targetRequestAccountDetailIds}
          done={() => {
            setDisableKeysSlideoutOpen(false);
            setReloadStats(new Date());
          }}
          cancel={() => setDisableKeysSlideoutOpen(false)}
          defaultReason={t('Disable keys for {account}', {
            account: account.login,
          })}
        />
      </SlidingPane>
      <SlidingPane
        isOpen={editAccountDetailsSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setEditAccountDetailsSlideoutOpen(false)}
      >
        <EditAccountDetails
          account={account}
          done={() => {
            setEditAccountDetailsSlideoutOpen(false);
            fetchAccount();
          }}
          cancel={() => setEditAccountDetailsSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  ) : (
    <Loading />
  );
}

export default Account;
